import React from 'react';

import classNames from 'classnames';
import { systemOfMeasurementType } from 'Models/Enums';
import { UnitConverter, UnitLabels } from 'Util/UnitOfMeasurementUtils';
import ErrorMessage from 'Views/Components/New/ErrorMessage';
import { NumberInput } from 'Views/Components/New/NumberInput';

interface Props {
	unitType: systemOfMeasurementType;
	value: number;
	onChange: (value: number) => void;
	preLabel: string;
	required?: boolean;
	error?: string;
}

/**
 * Length/width/height input for freight dimensions. Uses the New NumberInput.
 *
 * - Metric: a single input in millimetres.
 * - Imperial: connected feet + inches inputs, stored/emitted as a total number
 *   of inches (matching the value contract the callers expect).
 */
export default function MeasurementInput({ unitType, value, onChange, preLabel, required, error }: Props) {
	if (unitType === 'METRIC') {
		return (
			<NumberInput
				label={preLabel}
				placeholder="0"
				required={required}
				allowNegative={false}
				endAdornment={UnitLabels.length.millimeters}
				value={value || undefined}
				onChange={input => onChange(input ?? 0)}
				error={error}
			/>
		);
	}

	const { feet, inches } = value ? UnitConverter.inchesToFeetAndInches(value) : { feet: 0, inches: 0 };

	const onFeetChange = (input: number | undefined) => {
		onChange(UnitConverter.feetToInches(input ?? 0) + inches);
	};

	const onInchesChange = (input: number | undefined) => {
		onChange(UnitConverter.feetToInches(feet) + (input ?? 0));
	};

	// The error message is rendered once below the connected control, so only the error
	// border styling is applied to the individual feet/inches inputs.
	const errorClassName = error ? '!border-red-400 !bg-red-50 !text-red-900' : undefined;

	return (
		<div className="flex flex-col gap-1">
			<label className="text-sm text-gray-800">
				{preLabel}
				{required && <span className="text-[var(--colour-brand-primary)]"> *</span>}
			</label>
			{/* Feet + inches rendered as a single connected control. */}
			<div className="flex flex-row">
				<div className="flex-1 min-w-0">
					<NumberInput
						className={classNames('rounded-r-none', errorClassName)}
						placeholder="0"
						allowDecimals={false}
						allowNegative={false}
						endAdornment={UnitLabels.length.feet}
						value={value ? feet : undefined}
						onChange={onFeetChange}
					/>
				</div>
				<div className="flex-1 min-w-0 -ml-px">
					<NumberInput
						className={classNames('rounded-l-none', errorClassName)}
						placeholder="0"
						allowDecimals={false}
						allowNegative={false}
						endAdornment={UnitLabels.length.inches}
						value={value ? inches : undefined}
						onChange={onInchesChange}
					/>
				</div>
			</div>
			<ErrorMessage error={error} />
		</div>
	);
}
